import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { EmptyState, LoadingPanel } from "./Feedback";

export type Column<T> = {
  key: string;
  header: string;
  /** Right-align numeric columns so digits line up down the column. */
  align?: "left" | "right";
  width?: string;
  render: (row: T) => ReactNode;
};

/**
 * One table for every admin list screen.
 *
 * Wide tables scroll inside their own container rather than pushing the page
 * sideways, and the header stays put while the body scrolls.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isPending,
  empty,
  onRowClick,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  isPending?: boolean;
  empty?: { title: string; description?: string; icon?: ReactNode };
  onRowClick?: (row: T) => void;
}) {
  if (isPending) return <LoadingPanel label="Memuat tabel" />;

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={empty?.icon}
        title={empty?.title ?? "Belum ada data"}
        description={empty?.description}
      />
    );
  }

  return (
    <div className="scrollbar-slim overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        <thead>
          <tr className="border-hairline border-b">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={column.width ? { width: column.width } : undefined}
                className={cn(
                  "text-content-subtle px-4 py-2.5 text-2xs font-bold tracking-wide uppercase",
                  column.align === "right" ? "text-right" : "text-left",
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                "border-hairline border-b last:border-0",
                onRowClick && "hover:bg-surface-sunken cursor-pointer",
              )}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cn(
                    "px-4 py-3 align-middle",
                    column.align === "right" && "text-right",
                  )}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Consistent date rendering across every table. */
export function formatDate(value: string | null | undefined, withTime = false) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
}
